import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RecordingService, Recording } from '../services/RecordingService';
import { AudioWaveform } from '../components/AudioWaveform';
import { PlaybackModal } from '../components/RecordingsList';
import { AlertCircle } from 'lucide-react';

export const SharedRecordingPage: React.FC = () => {
  const { recordingId } = useParams<{ recordingId: string }>();
  const navigate = useNavigate();
  const [recording, setRecording] = useState<Recording | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPlayback, setShowPlayback] = useState(false);
  const recordingService = new RecordingService();

  useEffect(() => {
    const loadRecording = async () => {
      if (!recordingId) {
        setError('Recording ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await recordingService.getSharedRecording(recordingId);
        
        if (!data) {
          setError('Recording not found or not shared');
          setLoading(false);
          return;
        }

        setRecording(data);
        setShowPlayback(true);
        setLoading(false);
      } catch (error) {
        console.error('Error loading recording:', error);
        setError('Failed to load recording');
        setLoading(false);
      }
    };

    loadRecording();
  }, [recordingId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-kod-purple-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  if (error || !recording) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-kod-purple-900 p-4">
        <div className="flex items-center gap-2 text-red-500 mb-4">
          <AlertCircle className="w-8 h-8" />
          <h2 className="text-xl font-semibold">Error</h2>
        </div>
        <p className="text-white/70 text-center">{error || 'Recording not found'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kod-purple-900 flex items-center justify-center p-4">
      {showPlayback && (
        <div className="w-full max-w-4xl">
          <PlaybackModal
            recording={recording}
            onClose={() => navigate('/')}
          />
        </div>
      )}
    </div>
  );
};